// Modules
import React, { useContext } from 'react';
import { sortNcdmsStatewideFirst } from 'utilities/ncdmUtils';
import { useErrorHandler } from 'react-error-boundary';
import { handleFetchError } from 'utilities/utils';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import PlayForWorkIcon from '@mui/icons-material/PlayForWork';
import Button from '@mui/material/Button';
import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    useGridApiRef,
} from '@mui/x-data-grid-pro';

// Utils:
import TopicConfigContext from 'utilities/topicConfigContext';

// Config:
import config from 'config/config';
import { exportAsCsv } from '../../utilities/ncdmUtils';

// Components:
import CustomDataGridProFooter from '../UI/CustomDataGridProFooter';

// Styles:
const styles = {
    '&.MuiGrid-root': {
        paddingBottom: '1rem',
    },
    '&.MuiDataGrid-root': {
        fontSize: '0.8rem',
    },
    '&.MuiDataGridPro-footerContainer': {
        borderTop: 'none',
    },
    '&.MuiTypography-body1': {
        margin: 1,
    },
    '&.MuiFormControl-root': {
        margin: 1,
        minWidth: 300,
    },
    '&.MuiTypography-subtitle2': {
        margin: '0 1rem',
        width: '95%',
    },
    '&.MuiButton-root': {
        color: 'black',
    },
};

const CustomToolbar = ({ apiRef, ncdmSelections, columnHeaders }) => {
    // Methods:
    const handleClick = () => {
        const uiCsv = apiRef.current.getDataAsCsv();

        const year =
            ncdmSelections.year === '0' ? 'All years' : ncdmSelections.year;

        const title = `${ncdmSelections.indicator} (${year})`;

        exportAsCsv({
            uiCsv,
            title,
            subtitle: ncdmSelections.measure,
            columnHeaders,
        });
    };

    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton sx={styles} />
            <GridToolbarFilterButton sx={styles} />
            <Button
                size="small"
                onClick={handleClick}
                className="export-button"
            >
                {' '}
                <PlayForWorkIcon />
                EXPORT
            </Button>
        </GridToolbarContainer>
    );
};

// Main export component:

const NCDMContent = ({
    ncdmContent,
    setNCDMContent,
    ncdmSelections,
    topicNcdms,
    setNCDMSelections,
    formatColumns,
    setMissingSuppressedModalOpen,
}) => {
    // Hooks:
    const handleError = useErrorHandler();
    const apiRef = useGridApiRef();

    // Context:
    const { tab: currentTab } = useContext(TopicConfigContext);

    // Constants:
    const GET_ALL_YEARS_SELECT_VALUE = '0'; // '0' is used so the SPROC understands to return all data years regardless of topic.

    // Methods:
    const fetchContent = (year) => {
        const apiNcdmDataEndpoint = `${config.getNcdmDataEndpoint}?id=${ncdmSelections.id}&year=${year}`;
        fetch(apiNcdmDataEndpoint)
            .then(handleFetchError)
            .then((res) => res.json())
            .then((json) => {
                const { data } = json;
                setNCDMContent(data);
            })
            .catch(handleError);
    };

    const indicators = topicNcdms.map((ncdm) => ncdm.indicator);
    const currentIndicator = topicNcdms.find(
        (ncdm) => ncdm.indicator === ncdmSelections.indicator
    );

    const currentMeasure = currentIndicator
        ? currentIndicator.measures.find(
              (measure) => measure.measure === ncdmSelections.measure
          )
        : null;

    const ncdmDataYears = currentMeasure ? currentMeasure.ncdmDataYears : null;

    return (
        <>
            <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
                sx={styles}
            >
                <Grid item>
                    <Typography variant="body1" sx={styles}>
                        {currentTab.ncdmSettings.textSubheading}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <FormControl variant="outlined" sx={styles} size="small">
                        <InputLabel htmlFor="ncdm-select-indicator">
                            Indicator
                        </InputLabel>
                        <Select
                            id="ncdm-measure-select"
                            value={ncdmSelections.indicator}
                            onChange={(e) => {
                                setNCDMContent(null);
                                setNCDMSelections({
                                    indicator: e.target.value,
                                });
                            }}
                            label="Indicator"
                            inputProps={{
                                name: 'indicator',
                                id: 'ncdm-select-indicator',
                            }}
                        >
                            {indicators.map((indicator) => (
                                <MenuItem
                                    key={indicator}
                                    value={indicator}
                                    sx={{ whiteSpace: 'unset' }}
                                >
                                    {indicator}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                {ncdmSelections.indicator ? (
                    <Grid item xs={12}>
                        <FormControl
                            variant="outlined"
                            sx={styles}
                            size="small"
                        >
                            <InputLabel htmlFor="ncdm-select-measure">
                                Measure
                            </InputLabel>
                            <Select
                                id="ncdm-measure-select" // TODO: In place because MUI style overrides for 'white-space' on this particular 'Select' element do not seem to be acknowledged.
                                value={ncdmSelections.measure || ''}
                                onChange={(e, props) => {
                                    setNCDMContent(null);
                                    setNCDMSelections({
                                        indicator: ncdmSelections.indicator,
                                        measure: e.target.value,
                                        id: props.props['data-id'],
                                    });
                                }}
                                label="Measure"
                                inputProps={{
                                    name: 'measure',
                                    id: 'ncdm-select-measure',
                                }}
                            >
                                {topicNcdms
                                    .find(
                                        (indicator) =>
                                            indicator.indicator ===
                                            ncdmSelections.indicator
                                    )
                                    .measures.map((measure) => (
                                        <MenuItem
                                            key={measure.id}
                                            data-id={measure.id}
                                            value={measure.measure}
                                            sx={{ whiteSpace: 'unset' }}
                                        >
                                            {measure.measure}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>
                ) : null}
                {ncdmSelections.measure ? (
                    <Grid item xs={12}>
                        <FormControl
                            variant="outlined"
                            sx={styles}
                            size="small"
                        >
                            <InputLabel htmlFor="ncdm-select-year">
                                Year
                            </InputLabel>
                            <Select
                                value={ncdmSelections.year || ''}
                                onChange={(e) => {
                                    // Check if attempting to fetch an empty string and return
                                    if (e.target.value === '') {
                                        return;
                                    }
                                    fetchContent(e.target.value);
                                    setNCDMSelections((prevSelections) => ({
                                        ...prevSelections,
                                        year: e.target.value,
                                    }));
                                }}
                                sx={styles}
                                label="Year"
                                inputProps={{
                                    name: 'year',
                                    id: 'ncdm-select-year',
                                }}
                            >
                                <MenuItem
                                    key="all-years"
                                    value={GET_ALL_YEARS_SELECT_VALUE}
                                >
                                    All Years
                                </MenuItem>
                                {ncdmDataYears
                                    ? ncdmDataYears.map((year) => (
                                          <MenuItem key={year} value={year}>
                                              {year}
                                          </MenuItem>
                                      ))
                                    : null}
                            </Select>
                        </FormControl>
                    </Grid>
                ) : null}
            </Grid>
            <Grid container direction="row" justifyContent="center">
                {ncdmContent && (
                    <Grid item xs={12}>
                        <Typography sx={styles} variant="subtitle2">
                            {`${ncdmSelections.indicator}: ${
                                ncdmSelections.measure
                            } for ${
                                ncdmSelections.year === '0'
                                    ? ' all years'
                                    : ncdmSelections.year
                            }`}
                        </Typography>
                        <div
                            style={{
                                height: 860,
                                width: '95%',
                                padding: '0',
                                overflow: 'hidden',
                                margin: '1rem',
                            }}
                        >
                            <DataGridPro
                                apiRef={apiRef}
                                disableColumnMenu
                                disableSelectionOnClick
                                rows={sortNcdmsStatewideFirst(
                                    ncdmContent,
                                    currentTab.ncdmSettings?.sortColumn
                                )}
                                columns={formatColumns(
                                    currentMeasure.columns,
                                    currentTab.ncdmSettings?.sortColumn
                                )}
                                rowHeight={config.ncdmTableRowHeight}
                                scrollbarSize={25}
                                pageSize={config.tablePageSize}
                                sx={styles}
                                components={{
                                    Toolbar: CustomToolbar,
                                    Footer: CustomDataGridProFooter,
                                }}
                                componentsProps={{
                                    footer: { setMissingSuppressedModalOpen },
                                    toolbar: {
                                        apiRef,
                                        ncdmSelections,
                                        columnHeaders: currentMeasure.columns,
                                    },
                                }}
                            />
                        </div>
                    </Grid>
                )}
            </Grid>
        </>
    );
};

export default NCDMContent;
